import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Input } from '@factorialco/f0-react/dist/experimental'
import { RichTextEditor, RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'

import { observer } from 'mobx-react'

import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import DirectUploader from 'modules/trainings/components/Files/Uploaders/DirectUploader'
import { ContentEditorProps } from 'modules/trainings/components/content/types'
import { AttachedFile } from 'modules/trainings/lib/content/AttachedFile'
import { PageBlock } from 'modules/trainings/lib/content/PageBlock'

export const PageBlockEditor = observer(
  ({ contentItem, trainingId }: ContentEditorProps<PageBlock>) => {
    const descriptionRichTextEditorRef = React.useRef<RichTextEditorHandle>(null)
    const {
      editorConfig,
      canUseRichTextEditor,
      loading: richTextEditorLoading,
    } = useRichTextEditor()

    // Create a File object from existing thumbnail for display
    const initialFile = contentItem.thumbnail.isEmpty()
      ? undefined
      : contentItem.thumbnail.toFileObject('image/jpeg')

    return (
      <>
        <Input
          label={i18n.t('trainings.content.block.page.title_label')}
          onChange={(value: string) => {
            contentItem.title = value
          }}
          placeholder={i18n.t('trainings.content.block.page.title_placeholder')}
          value={contentItem.title}
          required
        />

        <Box className="gap-2">
          <Box className="gap-1">
            <label className="font-medium text-f1-foreground-secondary">
              {i18n.t('trainings.content.block.page.thumbnail_label')}
            </label>
            <span className="text-base text-f1-foreground-secondary">
              {i18n.t('trainings.content.block.page.thumbnail_description')}
            </span>
          </Box>
          <DirectUploader
            onUpload={async (uploadData) => {
              contentItem.thumbnail = AttachedFile.fromUpload(
                uploadData.file,
                uploadData.signedId || ''
              )
            }}
            onDelete={() => {
              contentItem.thumbnail.clear()
            }}
            description={i18n.t('trainings.content.block.page.thumbnail_description')}
            fileTypes={['image' as const]}
            resourceType={'Trainings::Training'}
            resourceId={String(trainingId)}
            initialFile={initialFile}
          />
        </Box>

        <Box className="gap-2">
          <Box className="gap-1">
            <label className="font-medium text-f1-foreground-secondary">
              {i18n.t('trainings.content.block.page.description_label')}
            </label>
          </Box>
          <Box style={{ position: 'relative', overflow: 'hidden' }}>
            {richTextEditorLoading ? (
              <RichTextEditor.Skeleton />
            ) : canUseRichTextEditor ? (
              <div
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onFocus={(e) => e.stopPropagation()}
              >
                <RichTextEditor
                  ref={descriptionRichTextEditorRef}
                  {...editorConfig}
                  onChange={(data) => {
                    contentItem.description = data?.value || ''
                  }}
                  placeholder={i18n.t('trainings.content.block.page.description_placeholder')}
                  initialEditorState={{ content: contentItem.description || '' }}
                  title={i18n.t('trainings.content.block.page.description_label')}
                />
              </div>
            ) : (
              <textarea
                value={contentItem.description}
                onChange={(e) => {
                  contentItem.description = e.target.value
                }}
                placeholder={i18n.t('trainings.content.block.page.description_placeholder')}
                className="w-full p-3 border border-gray-300 rounded-md resize-vertical min-h-[120px]"
              />
            )}
          </Box>
        </Box>
      </>
    )
  }
)
